import styled from '@emotion/styled';
import { Button } from 'antd';

interface OpenProps {
  isOpen?: boolean;
}

export const HamburgerMenuContainer = styled.div`
  position: fixed;
  top: 52px;
  left: 64px;
  z-index: 1000;

  @media (max-width: 768px) {
    top: 32px;
    left: 32px;
  }

  @media (max-width: 480px) {
    top: 32px;
    left: 32px;
  }
`;

export const MenuToggle = styled(Button)<OpenProps>`
  width: 50px;
  height: 50px;
  background: ${({ isOpen }) => (isOpen ? '#f2f2f2' : 'transparent')};
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    background: ${({ isOpen }) =>
      isOpen ? '#f2f2f2' : 'transparent'} !important;
    border: none !important;
  }

  &:active {
    transform: scale(0.95);
  }

  &.ant-btn {
    padding: 0;

    > span {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;

export const Hamburger = styled.div<OpenProps>`
  width: 30px;
  height: 18px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  span {
    display: block;
    height: 2px;
    width: 100%;
    background-color: #0e2a27;
    border-radius: 1px;
    transition: all 0.3s ease;
    position: absolute;
    left: 0;

    &:nth-of-type(1) {
      top: ${({ isOpen }) => (isOpen ? '50%' : '0')};
      transform: ${({ isOpen }) =>
        isOpen ? 'translateY(-50%) rotate(45deg)' : 'none'};
    }

    &:nth-of-type(2) {
      top: 50%;
      transform: translateY(-50%);
      opacity: ${({ isOpen }) => (isOpen ? 0 : 1)};
    }

    &:nth-of-type(3) {
      bottom: ${({ isOpen }) => (isOpen ? '50%' : '0')};
      transform: ${({ isOpen }) =>
        isOpen ? 'translateY(50%) rotate(-45deg)' : 'none'};
    }
  }
`;

export const Overlay = styled.div<OpenProps>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  z-index: -1;
  animation: ${({ isOpen }) => (isOpen ? 'fadeIn 0.3s ease' : 'none')};

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const MenuItems = styled.div<OpenProps>`
  position: absolute;
  top: calc(100% + 12px);
  left: 0;
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: visible;
  transform: ${({ isOpen }) =>
    isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const MenuItemGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
`;

export const ChildrenContainer = styled.div<{ $isVisible: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
  transform: ${({ $isVisible }) =>
    $isVisible ? 'translateX(0)' : 'translateX(-20px)'};
  transition:
    opacity 0.3s ease,
    transform 0.3s ease,
    visibility 0.3s ease;
`;

export const StyledMenuItem = styled(Button)<{
  $color: string;
  $colorHover: string;
  $bgHover: string;
}>`
  width: 250px;
  height: auto;
  min-height: 48px;
  padding: 0;
  background: white;
  border: none;
  border-radius: 24px;
  text-align: left;
  font-size: 1rem;
  color: ${({ $color }) => $color};
  cursor: pointer;
  transition:
    width 0.3s ease-in-out,
    background-color 0.3s ease;
  overflow: hidden;
  white-space: nowrap;
  display: flex !important;
  align-items: center;

  &:hover:not(:disabled) {
    background-color: ${({ $bgHover }) => $bgHover} !important;
    border: none !important;
    color: ${({ $colorHover }) => $colorHover} !important;
    width: 320px;
  }

  &:disabled {
    cursor: not-allowed;
    color: ${({ $color }) => $color} !important;
    background: #e6e6e6 !important;
  }

  &.ant-btn {
    display: flex !important;
    align-items: center;

    > span {
      display: flex;
      align-items: center;
      width: 100%;
    }
  }
`;

export const MenuItemContent = styled.div`
  padding: 14px 16px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 20px;
`;

export const MenuItemTitle = styled.div`
  font-weight: 500;
  font-size: 1rem;
  transition: color 0.3s ease;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  ${StyledMenuItem}:hover:not(:disabled) & {
    color: #f2f2f2;
  }
`;

export const MenuItemModule = styled.span`
  font-weight: normal;
  font-size: 1rem;
  text-transform: uppercase;
  opacity: 0.7;
  transition: all 0.3s ease;
  margin-left: 4px;

  ${StyledMenuItem}:hover:not(:disabled) & {
    opacity: 0;
    width: 0;
    margin-left: 0;
    overflow: hidden;
  }
`;

export const MenuItemDescription = styled.div`
  font-size: 1rem;
  color: #666;
  line-height: 1.4;
  opacity: 0;
  max-width: 0;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  ${StyledMenuItem}:hover:not(:disabled) & {
    opacity: 1;
    max-width: 240px;
    color: rgba(255, 255, 255, 0.9);
  }
`;
